// ============================================================
// caseNav.constants.ts
// The case page's GROUPED section nav (Stage 1 shell — approved mockup
// casevo-case-page-overview.html "CA132B"). Display-only grouping over the
// EXISTING headings: VIEWING_PANE_HEADINGS stays the single source for which
// sections exist, their toggles, admin gating and the setup chain. Every
// heading key appears here exactly once (+ the Overview landing), so no
// section is orphaned; a heading missing from this table would still render
// via the "Other" fallback group in CaseSectionNav.
// ============================================================
import type { CaseSectionKey } from '../types/case.types'
import type { SettingsIconName } from '../components/settings/settingsUi'

export interface CaseNavEntry {
  key: CaseSectionKey
  label: string
  icon: SettingsIconName
}
export interface CaseNavGroup {
  // null = ungrouped (Overview sits above the first heading).
  label: string | null
  items: CaseNavEntry[]
}

export const CASE_NAV_GROUPS: CaseNavGroup[] = [
  { label: null, items: [{ key: 'overview', label: 'Overview', icon: 'grid' }] },
  {
    label: 'People',
    items: [
      { key: 'clientSource', label: 'Client source', icon: 'source' },
      { key: 'clientDetails', label: 'Client details', icon: 'user' },
      { key: 'witnesses', label: 'Witnesses', icon: 'eye' },
    ],
  },
  {
    label: 'Accident & injury',
    items: [
      { key: 'accidentDetails', label: 'Accident details', icon: 'car' },
      { key: 'injuryDetails', label: 'Injury details', icon: 'heart' },
    ],
  },
  {
    label: 'Other side',
    items: [
      { key: 'defendant', label: 'Defendant', icon: 'shield' },
      { key: 'tpi', label: 'TPI (insurer)', icon: 'umbrella' },
      { key: 'tps', label: 'TPS', icon: 'scale' },
    ],
  },
  {
    label: 'Medical',
    items: [
      { key: 'medicalAgency', label: 'Medical agency', icon: 'agency' },
      { key: 'medicalExpert', label: 'Medical expert', icon: 'steth' },
    ],
  },
  {
    label: 'Case file',
    items: [
      { key: 'documents', label: 'Documents', icon: 'file' },
      { key: 'deadlines', label: 'Deadlines', icon: 'flag' },
      { key: 'funding', label: 'Funding', icon: 'pound' },
      // Invoicing Stage 1 — the case's bills, WIP-ready-to-bill + disbursements.
      { key: 'billing', label: 'Billing', icon: 'bill' },
      // Stage 4 round 2a — CFA + settlement inputs + the deductions preview.
      { key: 'settlement', label: 'Settlement', icon: 'percent' },
      { key: 'linkedCases', label: 'Linked cases', icon: 'link' },
    ],
  },
  {
    label: 'Records',
    items: [
      { key: 'activity', label: 'Activity', icon: 'pulse' },
      { key: 'history', label: 'History', icon: 'history' },
      { key: 'automatic', label: 'Case control', icon: 'cog' },
    ],
  },
]

// key → { label, icon, group } for the section page header + lookups.
export const CASE_NAV_INDEX: Record<string, CaseNavEntry & { group: string | null }> = Object.fromEntries(
  CASE_NAV_GROUPS.flatMap((g) => g.items.map((it) => [it.key, { ...it, group: g.label }])),
)
